import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { ClickAwayListener, MenuList, useTheme, useThemeProps } from "@mui/material";
import { cloneElement, forwardRef, useRef, useState } from "react";
import { MoreVert } from "../../icons/index.js";
import { actionBarName, useUtilityClasses } from "./classes.js";
import { List } from "./components/List.js";
import { MenuButton } from "./components/MenuButton.js";
import { MenuTooltip } from "./components/MenuTooltip.js";
import { Popper } from "./components/Popper.js";
import { Root } from "./components/Root.js";
import { Stack } from "./components/Stack.js";
import { TruncateList } from "./components/TruncateList.js";
import { defaultComputeGap } from "./defaultComputeGap.js";
import { useActionBar } from "./hooks/useActionBar.js";
import { useActionItems } from "./hooks/useActionItems.js";
const defaultMenuIcon = /* @__PURE__ */ jsx(MoreVert, { fontSize: "inherit" });
const ActionBar = forwardRef(function ActionBar2(inProps, ref) {
  const props = useThemeProps({ name: actionBarName, props: inProps });
  const _theme = useTheme();
  const [hiddenCount, setHiddenCount] = useState(0);
  const {
    sx,
    slots = {},
    testId = `${actionBarName}-root`,
    slotProps = {},
    className = "",
    actions,
    onClick,
    children,
    disabled,
    onChanged,
    size = "medium",
    limit = Number.POSITIVE_INFINITY,
    density,
    menuMode = "textIcon",
    listMode = "iconOnly",
    menuIcon = defaultMenuIcon,
    computeGap = defaultComputeGap,
    menuTooltip,
    ...rest
  } = props;
  const gap = typeof computeGap === "number" ? computeGap : typeof computeGap === "function" ? computeGap(density) : 0;
  const buttonRef = useRef(null);
  const { menuOpen, onOpen, onClose, parentOnClick } = useActionBar({
    actions,
    buttonRef,
    onChanged,
    onClick
  });
  const { buttonItems, menuItems, showMenu, endItem } = useActionItems({
    children,
    hiddenCount,
    limit
  });
  const _internal = {
    gap,
    menuMode,
    parentOnClick,
    renderInTruncateList: limit === "auto",
    size
  };
  const ownerState = {
    ...props,
    limit,
    menuMode,
    size
  };
  const utilityClasses = useUtilityClasses(ownerState);
  const RootSlot = slots.root || Root;
  const ListSlot = slots.list || List;
  const StackSlot = slots.stack || Stack;
  const PopperSlot = slots.popper || Popper;
  const MenuButtonSlot = slots.menuButton || MenuButton;
  const MenuTooltipSlot = slots.menuTooltip || MenuTooltip;
  const TruncateListSlot = slots.truncateList || TruncateList;
  const items = buttonItems.map((item, key) => {
    const clonedDisabled = item.props.disabled ?? disabled;
    const clonedProps = {
      _internal: {
        ..._internal,
        menuMode: listMode
      },
      disabled: clonedDisabled,
      key: `${actionBarName}-item-${key}`
    };
    return cloneElement(item, clonedProps);
  });
  const menuButton = /* @__PURE__ */ jsx(
    MenuButtonSlot,
    {
      "data-testid": `${actionBarName}-menuBtn`,
      disabled,
      size,
      ...slotProps.menuButton,
      className: utilityClasses.menuButton,
      onClick: onOpen,
      ownerState,
      ref: buttonRef,
      children: menuIcon
    }
  );
  return /* @__PURE__ */ jsxs(
    RootSlot,
    {
      "data-testid": testId,
      ...slotProps.root,
      ...rest,
      className: `${className} ${utilityClasses.root}`,
      "data-tidal": "ActionBar",
      ownerState,
      ref,
      sx,
      children: [
        limit === "auto" ? /* @__PURE__ */ jsx(
          TruncateListSlot,
          {
            "data-testid": `${actionBarName}-truncateList`,
            ...slotProps.truncateList,
            className: utilityClasses.truncateList,
            computeGap,
            density,
            onChangeHiddenItemsCount: setHiddenCount,
            ownerState,
            ShowMore: null,
            children: items
          }
        ) : /* @__PURE__ */ jsx(
          StackSlot,
          {
            direction: "row",
            gap,
            ...slotProps.stack,
            className: utilityClasses.stack,
            ownerState,
            children: items
          }
        ),
        showMenu && /* @__PURE__ */ jsxs(Fragment, { children: [
          menuTooltip ? /* @__PURE__ */ jsx(
            MenuTooltipSlot,
            {
              className: utilityClasses.menuTooltip,
              title: menuTooltip,
              ...slotProps.menuTooltip,
              ownerState,
              children: menuButton
            }
          ) : menuButton,
          /* @__PURE__ */ jsx(
            PopperSlot,
            {
              placement: "bottom-start",
              ...slotProps.popper,
              anchorEl: buttonRef.current,
              className: utilityClasses.popper,
              open: menuOpen,
              children: /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: onClose, children: /* @__PURE__ */ jsx(
                ListSlot,
                {
                  "data-testid": `${actionBarName}-list`,
                  ...slotProps.list,
                  className: utilityClasses.list,
                  ownerState,
                  children: /* @__PURE__ */ jsx(MenuList, { children: menuItems.map(
                    (item, key) => cloneElement(item, {
                      _internal,
                      disabled,
                      key,
                      mode: "menu"
                    })
                  ) })
                }
              ) })
            }
          )
        ] }),
        endItem && cloneElement(endItem, {
          _internal: {
            ..._internal,
            menuMode: listMode
          },
          disabled
        })
      ]
    }
  );
});
export {
  ActionBar
};
//# sourceMappingURL=ActionBar.js.map
